import { t, useLanguage } from "@/lib/i18n";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function TreeOrderAdmin({ embedded = false }) {
  useLanguage();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [persons, setPersons] = useState([]);
  const [fatherId, setFatherId] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const load = useCallback(async () => {
    setMessage("");
    try {
      const [config, members] = await Promise.all([api.get("/settings/tree-order"), api.get("/persons")]);
      setSettings(config.data);
      setPersons(members.data);
      setFatherId(members.data.find((p) => p.father_id)?.father_id || "");
    } catch (error) { setMessage("Could not load sibling order. Please try again."); }
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role, load]);
  const fathers = useMemo(() => {
    const byId = new Map(persons.map((p) => [p.id, p]));
    return [...new Set(persons.map((p) => p.father_id).filter(Boolean))].map((id) => ({
      id, name: byId.get(id)?.full_name || `Unavailable father (${id})`,
      lineage: [byId.get(id)?.religion, byId.get(id)?.caste, byId.get(id)?.gotra].filter(Boolean).join(" / "),
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [persons]);
  if (user?.role !== "admin") return <Navigate to="/app" replace />;
  const siblings = persons.filter((p) => p.father_id === fatherId);
  const save = async (event) => {
    event.preventDefault();
    setSaving(true); setMessage("");
    try {
      const { data } = await api.put("/settings/tree-order", settings);
      setSettings(data);
      setMessage("Sibling order saved. Open the tree to see the updated order.");
    } catch (error) {
      setMessage(formatApiErrorDetail(error.response?.data?.detail) || "Could not save sibling order.");
    } finally { setSaving(false); }
  };
  const selectClass = "block w-full border border-stone-400 rounded-md bg-white p-2 mt-1";
  const Container = embedded ? "section" : "main";
  const Heading = embedded ? "h2" : "h1";
  return <Container className={embedded ? "" : "min-h-screen bg-[#FAF7F2] p-6"}>
    <div className="max-w-4xl mx-auto space-y-6">
      {!embedded && <Link to="/app" className="text-amber-800 underline">{t("Back to tree")}</Link>}
      <Heading className="font-serif text-3xl">{t("Sibling display order")}</Heading>
      <p>{t("The criterion and direction apply to all families. Birth-date ordering is used only when every child of a father has a valid birth date. Otherwise, that entire sibling group uses its order numbers.")}</p>
      {message && <p role="status">{t(message)}</p>}
      {!settings && (message ? <Button onClick={load}>{t("Try again")}</Button> : <p>{t("Loading sibling order…")}</p>)}
      {settings && <form onSubmit={save} className="space-y-6 bg-white border rounded-xl p-5">
        <fieldset disabled={saving} className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="sibling-criterion">{t("Display criterion")}</Label>
              <select id="sibling-criterion" className={selectClass} value={settings.criterion}
                onChange={(e) => setSettings({ ...settings, criterion: e.target.value })}>
                <option value="birth_date">{t("Birth date (fallback to order number)")}</option>
                <option value="order_number">{t("Defined order number")}</option>
              </select>
            </div>
            <div><Label htmlFor="sibling-direction">{t("Direction")}</Label>
              <select id="sibling-direction" className={selectClass} value={settings.direction}
                onChange={(e) => setSettings({ ...settings, direction: e.target.value })}>
                <option value="asc">{t("Ascending — oldest first / lowest number first")}</option>
                <option value="desc">{t("Descending — youngest first / highest number first")}</option>
              </select>
            </div>
          </div>
          <div className="space-y-3">
            <h2 className="text-xl font-semibold">{t("Order numbers by father")}</h2>
            <p>{t("Default numbers follow member creation order. Edit them below using a unique positive whole number for each child of the same father. Numbers are retained when you switch criteria.")}</p>
            {fathers.length ? <>
              <Label htmlFor="sibling-father">{t("Father")}</Label>
              <select id="sibling-father" className={selectClass} value={fatherId} onChange={(e) => setFatherId(e.target.value)}>
                {fathers.map((father) => <option key={father.id} value={father.id}>{father.name}{father.lineage ? ` — ${father.lineage}` : ""}</option>)}
              </select>
              <div className="overflow-x-auto"><table className="w-full min-w-[440px] text-left">
                <thead><tr className="border-b border-stone-300"><th className="p-2">{t("Child")}</th><th className="p-2">{t("Birth date")}</th><th className="p-2">{t("Order number")}</th></tr></thead>
                <tbody>{siblings.map((person) => <tr key={person.id} className="border-b border-stone-200">
                  <td className="p-2">{person.full_name}</td><td className="p-2">{person.birth_date || t("Not provided")}</td>
                  <td className="p-2"><Input type="number" min="1" max="2147483647" step="1" required
                    aria-label={t("Order number for {0}", { 0: person.full_name })} data-testid={`sibling-order-${person.id}`}
                    value={settings.order_numbers[person.id] ?? ""}
                    onChange={(e) => setSettings({ ...settings, order_numbers: { ...settings.order_numbers,
                      [person.id]: e.target.value === "" ? "" : Number(e.target.value) } })} /></td>
                </tr>)}</tbody>
              </table></div>
            </> : <p>{t("No members with a recorded father yet.")}</p>}
          </div>
          <Button type="submit" disabled={saving}>{saving ? t("Saving…") : t("Save sibling order")}</Button>
        </fieldset>
      </form>}
    </div>
  </Container>;
}
